import { GetCustomerSettingsPractitionerQuery } from '../../graphql/camel'
import { UseSiteSettings } from '../../hooks/useSiteSettings'
import { isNotNull } from '../../utils/collectionTools'
import type {
  PractitionerSettings,
  PractitionerSettingsDataToSave,
  PractitionerSettingsDispensingOptionsFormFields,
  PractitionerSettingsInboundPatientFields,
  PractitionerSettingsPractitioner,
  PractitionerSettingsRebateFormFields,
} from './types'
import { PractitionerSettingsDispensaryEmailsFields } from './types'

export type SettingsDocumentsOnFile = Pick<
  PractitionerSettingsPractitioner,
  'achFormReceived' | 'taxIdFormReceived' | 'commissionsOptOutFormReceived'
>

export type SettingsDispensingOptions = PractitionerSettingsPractitioner['dispensingOptions']
export type SettingsDispensingOption =
  | SettingsDispensingOptions['inPractice']
  | SettingsDispensingOptions['dfhWebstore']

export type SettingsEnrolledInRebate = Pick<PractitionerSettingsPractitioner, 'enrolledInRebate'>

export type SettingsInboundPatient = Pick<
  PractitionerSettingsPractitioner,
  'optedOutOfDispensary' | 'optedOutOfInboundPatient'
>

export type SettingsDispensaryEmails = Pick<
  PractitionerSettingsPractitioner,
  'optedOutOfDispensary' | 'optedOutOfDispensaryOrderEmails'
>

export type RequiredForCommissionsKey = 'eft' | 'taxId'

export type DocumentsOnFileKey = RequiredForCommissionsKey

export interface DocumentOnFile {
  formTitle: string
  formUrl: string
  isReceived: boolean
}

export type DocumentOnFileNotReceived = DocumentOnFile & {
  isReceived: false
}
export type DocumentOnFileReceived = DocumentOnFile & {
  isReceived: true
}

export type DocumentsOnFile = Map<DocumentsOnFileKey, DocumentOnFile>

export interface DocumentsNeeded {
  neededForCommissions: Array<[RequiredForCommissionsKey, DocumentOnFileNotReceived]>
  receivedForCommissions: Array<[RequiredForCommissionsKey, DocumentOnFileReceived]>
}

export const buildDocumentsOnFile =
  ({ eftForm, taxIdForm }: Pick<NonNullable<UseSiteSettings>, 'eftForm' | 'taxIdForm'>) =>
  ({ achFormReceived, taxIdFormReceived }: SettingsDocumentsOnFile): DocumentsOnFile => {
    const documentsOnFile = new Map<DocumentsOnFileKey, DocumentOnFile>()
    const { label: eftFormLabel, link: eftFormLink } = eftForm || {}
    const { label: taxFormLabel, link: taxFormLink } = taxIdForm || {}
    if (eftFormLink) {
      documentsOnFile.set('eft', {
        formTitle: eftFormLabel || 'EFT',
        formUrl: eftFormLink,
        isReceived: achFormReceived,
      })
    }
    if (taxFormLink) {
      documentsOnFile.set('taxId', {
        formTitle: taxFormLabel || 'Tax ID',
        formUrl: taxFormLink,
        isReceived: taxIdFormReceived,
      })
    }
    return documentsOnFile
  }

export const defaultCommissionsOptOutLabel = 'Commissions Opt-Out'

export const isRequiredForCommissionsKey = (
  key: DocumentsOnFileKey,
): key is RequiredForCommissionsKey => key === 'eft' || key === 'taxId'

export const isDocumentNotReceived = (doc: DocumentOnFile): doc is DocumentOnFileNotReceived =>
  !doc.isReceived

export const isDocumentReceived = (doc: DocumentOnFile): doc is DocumentOnFileReceived =>
  doc.isReceived

export const isRequiredForCommissionsAndNotReceived = (
  entry: [DocumentsOnFileKey, DocumentOnFile],
): entry is [RequiredForCommissionsKey, DocumentOnFileNotReceived] =>
  isRequiredForCommissionsKey(entry[0]) && isDocumentNotReceived(entry[1])

export const isRequiredForCommissionsAndReceived = (
  entry: [DocumentsOnFileKey, DocumentOnFile],
): entry is [RequiredForCommissionsKey, DocumentOnFileReceived] =>
  isRequiredForCommissionsKey(entry[0]) && isDocumentReceived(entry[1])

export const getDispensingOptionsDefaultValues = (
  dispensingOptions: SettingsDispensingOptions,
): PractitionerSettingsDispensingOptionsFormFields => {
  const { inPractice, dfhWebstore } = dispensingOptions
  return {
    inPractice: parseDispensingOption(inPractice).isDefaultChecked,
    dfhWebstore: parseDispensingOption(dfhWebstore).isDefaultChecked,
  }
}

export const parseDispensingOption = ({
  isDesired,
  isEnabled,
}: SettingsDispensingOption): {
  isDefaultChecked: boolean
  isPending: boolean
} => {
  return {
    isDefaultChecked: !!isDesired,
    isPending: !!isDesired && !isEnabled,
  }
}

/**
 * @param isDesired Value from `profileInfo`
 * @param isChecked Value from form
 * @returns {{isDesired}|undefined}
 */
export const getDfhWebstoreOptionToSave = (
  { isDesired }: SettingsDispensingOptions['dfhWebstore'],
  { isChecked }: { isChecked: boolean },
): { isDesired: boolean } | undefined => {
  const isOptionUpdated = isDesired !== isChecked
  if (!isOptionUpdated) {
    return undefined
  }
  return {
    isDesired: !!isChecked,
  }
}

export const areCommissionsRelatedFormsNeeded = ({
  achFormReceived,
  taxIdFormReceived,
  optedOutOfDispensary,
  commissionsOptOutFormReceived,
}: PractitionerSettingsPractitioner): boolean =>
  (!achFormReceived || !taxIdFormReceived) &&
  !commissionsOptOutFormReceived &&
  !optedOutOfDispensary

export const inboundPatientShouldBeTrue = ({
  optedOutOfDispensary,
  optedOutOfInboundPatient,
}: SettingsInboundPatient): boolean => {
  if (isNotNull(optedOutOfDispensary) && isNotNull(optedOutOfInboundPatient)) {
    return !optedOutOfDispensary && !optedOutOfInboundPatient
  }
  if (isNotNull(optedOutOfInboundPatient)) {
    return !optedOutOfInboundPatient
  }
  return false
}

export const dispensaryOrderEmailsShouldBeTrue = ({
  optedOutOfDispensary,
  optedOutOfDispensaryOrderEmails,
}: SettingsDispensaryEmails): boolean => {
  if (isNotNull(optedOutOfDispensary) && isNotNull(optedOutOfDispensaryOrderEmails)) {
    return !optedOutOfDispensary && !optedOutOfDispensaryOrderEmails
  }
  if (isNotNull(optedOutOfDispensaryOrderEmails)) {
    return !optedOutOfDispensaryOrderEmails
  }
  return false
}

export const getDispensaryEmailsToSave = (
  {
    optedOutOfDispensary,
    optedOutOfDispensaryOrderEmails: currentlyOptedOutOfDispensaryOrderEmails,
  }: Pick<
    PractitionerSettingsPractitioner,
    'optedOutOfDispensaryOrderEmails' | 'optedOutOfDispensary'
  >,
  formValues: PractitionerSettingsDispensaryEmailsFields,
): Pick<PractitionerSettingsDataToSave, 'optedOutOfDispensaryOrderEmails'> | undefined => {
  if (optedOutOfDispensary || !('dispensaryOrderEmails' in formValues)) {
    return undefined
  }

  const optedOutOfDispensaryEmailsInForm = !formValues.dispensaryOrderEmails
  const isOptionConsideredUpdated =
    currentlyOptedOutOfDispensaryOrderEmails !== optedOutOfDispensaryEmailsInForm
  if (!isOptionConsideredUpdated) {
    return undefined
  }
  return {
    optedOutOfDispensaryOrderEmails: optedOutOfDispensaryEmailsInForm,
  }
}

export const getDispensaryEmailsDefaultValues = ({
  optedOutOfDispensary,
  optedOutOfDispensaryOrderEmails,
}: PractitionerSettingsPractitioner): PractitionerSettingsDispensaryEmailsFields => {
  return {
    dispensaryOrderEmails: dispensaryOrderEmailsShouldBeTrue({
      optedOutOfDispensary,
      optedOutOfDispensaryOrderEmails,
    }),
  }
}

export const getInboundPatientDefaultValues = ({
  optedOutOfDispensary,
  optedOutOfInboundPatient,
}: SettingsInboundPatient): PractitionerSettingsInboundPatientFields => {
  return {
    inboundPatient: inboundPatientShouldBeTrue({
      optedOutOfDispensary,
      optedOutOfInboundPatient,
    }),
  }
}

export const getInboundPatientToSave = (
  { optedOutOfDispensary, optedOutOfInboundPatient }: SettingsInboundPatient,
  formValues: PractitionerSettingsInboundPatientFields,
): Pick<PractitionerSettingsDataToSave, 'optedOutOfInboundPatient'> | undefined => {
  if (!('inboundPatient' in formValues)) {
    return undefined
  }

  const isChecked = !!formValues.inboundPatient
  const isOptionConsideredUpdated =
    inboundPatientShouldBeTrue({ optedOutOfDispensary, optedOutOfInboundPatient }) !== isChecked
  if (!isOptionConsideredUpdated) {
    return undefined
  }
  return {
    optedOutOfInboundPatient: !isChecked,
  }
}

export const getRebateDefaultValues = ({
  enrolledInRebate,
}: SettingsEnrolledInRebate): PractitionerSettingsRebateFormFields => {
  return { enrolledInRebate }
}

export const getRebateToSave = (
  { enrolledInRebate }: SettingsEnrolledInRebate,
  formValues: PractitionerSettingsRebateFormFields,
): Pick<PractitionerSettingsDataToSave, 'enrolledInRebate'> | undefined => {
  if (!('enrolledInRebate' in formValues)) {
    return undefined
  }

  const isChecked = !!formValues.enrolledInRebate
  const isOptionUpdated = enrolledInRebate !== isChecked
  if (!isOptionUpdated) {
    return undefined
  }
  return {
    enrolledInRebate: isChecked,
  }
}

const isSettingsWithPersonal = <
  T extends NonNullable<GetCustomerSettingsPractitionerQuery['getCustomerSettings']>,
>(
  value: T,
): value is T & {
  personal: NonNullable<
    NonNullable<GetCustomerSettingsPractitionerQuery['getCustomerSettings']>['personal']
  >
} => !!value.personal

const isSettingsWithPractitioner = <
  T extends NonNullable<GetCustomerSettingsPractitionerQuery['getCustomerSettings']>,
>(
  value: T,
): value is T & {
  practitioner: NonNullable<
    NonNullable<GetCustomerSettingsPractitionerQuery['getCustomerSettings']>['practitioner']
  >
} => !!value.practitioner

export function practitionerSettingsFromResponse(
  data: GetCustomerSettingsPractitionerQuery,
): PractitionerSettings | undefined {
  const { getCustomerSettings } = data
  if (!getCustomerSettings) {
    return undefined
  }
  if (!isSettingsWithPersonal(getCustomerSettings)) {
    console.error(`Incomplete customer settings response (missing \`personal\`)`)
    return undefined
  }
  if (!isSettingsWithPractitioner(getCustomerSettings)) {
    console.error(`Incomplete customer settings response (missing \`practitioner\`)`)
    return undefined
  }
  // return existing object to avoid requiring memoization
  return getCustomerSettings
}
